import { useState } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { api } from "../../services/api";
import MoneyInput from "./MoneyInput";
import { useToast } from "../../contexts/ToastContext";

const TIPO_ICON = { efectivo: "💵", "débito": "💳", "crédito": "💳", vales: "🎫" };

export default function TransferenciaSection({ cuentas, transferencias, setTransferencias, computedSaldos }) {
  const [open, setOpen] = useState(true);
  const [origen, setOrigen] = useState("");
  const [destino, setDestino] = useState("");
  const [monto, setMonto] = useState("");
  const [concepto, setConcepto] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saldoAlert, setSaldoAlert] = useState(null);
  const toast = useToast();

  const debitoCuentas = cuentas.filter(c => c.tipo === "débito" || c.tipo === "efectivo");
  const destinoCuentas = cuentas.filter(c => c.id !== origen && c.tipo !== "crédito");
  const saldoOrigen = origen && computedSaldos ? (computedSaldos[origen] || 0) : null;
  const canSave = origen && destino && origen !== destino && Number(monto) > 0;

  const handleCreate = async () => {
    if (!canSave || saving) return;
    if (saldoOrigen !== null && Number(monto) > saldoOrigen) {
      setSaldoAlert(`Saldo insuficiente (${fmt(saldoOrigen)})`);
      setTimeout(() => setSaldoAlert(null), 3000);
      return;
    }
    setSaving(true);
    try {
      const res = await api.transferencias.create({
        cuenta_origen: origen,
        cuenta_destino: destino,
        monto: Number(monto),
        concepto: concepto || "Transferencia",
      });
      // Optimistic update
      const newTransfer = Array.isArray(res) ? res[0] : res;
      if (newTransfer) setTransferencias(prev => [...prev, newTransfer]);
      setOrigen(""); setDestino(""); setMonto(""); setConcepto("");
      toast.show("✓ Transferencia realizada");
    } catch (err) {
      console.error("Transfer failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmDelete(null);
    try {
      await api.transferencias.remove(id);
      // Optimistic update — remover del array local
      setTransferencias(prev => prev.filter(t => t.id !== id));
      toast.show("✓ Transferencia eliminada");
    } catch (err) {
      console.error("Delete transfer failed:", err);
    }
  };

  const cuentaName = (id) => {
    const c = cuentas.find(ct => ct.id === id);
    if (!c) return "—";
    return `${TIPO_ICON[c.tipo] || "💳"} ${c.nombre}`;
  };

  const t = { pastel: "#EEF2FF", tint: "#C7D2FE", text: "#3730A3", base: "#6366F1", mid: "#818CF8" };

  return (
    <div style={{ marginBottom: 14, borderRadius: 16, border: `1.5px solid ${P.border}`, overflow: "hidden", background: P.surface }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%", padding: "14px 20px", background: t.pastel,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          border: "none", cursor: "pointer", fontFamily: FF,
          borderBottom: open ? `1.5px solid ${t.tint}` : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🔄</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: t.text }}>Transferencias</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: t.mid }}>{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "12px 20px 16px" }}>
          {/* Formulario nueva transferencia */}
          <div style={{ padding: "12px", background: P.bg, borderRadius: 12, border: `1px solid ${P.border}`, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.textSm, marginBottom: 8, fontFamily: FF }}>Nueva transferencia</div>

            {/* Origen */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: P.textXs, width: 40, flexShrink: 0, fontFamily: FF }}>De:</span>
              <select
                value={origen}
                onChange={(e) => setOrigen(e.target.value)}
                style={{ ...inputBase, flex: 1, fontSize: 12, padding: "7px 10px", cursor: "pointer" }}
              >
                <option value="">Seleccionar cuenta...</option>
                {debitoCuentas.map(c => (
                  <option key={c.id} value={c.id}>{TIPO_ICON[c.tipo]} {c.nombre}</option>
                ))}
              </select>
            </div>

            {/* Destino */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: P.textXs, width: 40, flexShrink: 0, fontFamily: FF }}>A:</span>
              <select
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                style={{ ...inputBase, flex: 1, fontSize: 12, padding: "7px 10px", cursor: "pointer" }}
              >
                <option value="">Seleccionar cuenta...</option>
                {destinoCuentas.map(c => (
                  <option key={c.id} value={c.id}>{TIPO_ICON[c.tipo]} {c.nombre}</option>
                ))}
              </select>
            </div>

            {/* Monto + concepto */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ position: "relative", width: 110, flexShrink: 0 }}>
                <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 11, pointerEvents: "none" }}>$</span>
                <MoneyInput
                  value={Number(monto) || 0}
                  onChange={(v) => setMonto(String(v))}
                  style={{ ...inputBase, paddingLeft: 22, fontWeight: 700, textAlign: "right", fontSize: 13 }}
                />
              </div>
              <input
                value={concepto} placeholder="Concepto (opcional)"
                onChange={(e) => setConcepto(e.target.value)}
                style={{ ...inputBase, flex: 1, fontSize: 12 }}
              />
            </div>

            {/* Botón crear */}
            <button type="button"
              onClick={(e) => { e.preventDefault(); handleCreate(); }}
              disabled={!canSave || saving}
              style={{
                width: "100%", padding: "10px", borderRadius: 10, border: "none",
                background: canSave ? t.base : P.border,
                color: canSave ? "#fff" : P.textXs,
                fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: canSave ? "pointer" : "default",
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? "Guardando..." : saldoOrigen !== null ? `Transferir (disp: ${fmt(saldoOrigen)})` : "Transferir"}
            </button>
            {saldoAlert && (
              <div style={{ marginTop: 6, padding: "6px 10px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 11, fontWeight: 600, color: "#DC2626", fontFamily: FF }}>{saldoAlert}</div>
            )}
          </div>

          {/* Lista de transferencias recientes */}
          {(transferencias || []).length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: P.textXs, marginBottom: 6, fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.05em" }}>Recientes</div>
              {(transferencias || []).slice(0, 5).map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px solid ${P.border}` }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#6366F118", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>🔄</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.concepto || "Transferencia"}
                    </div>
                    <div style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>
                      {cuentaName(t.cuenta_origen)} → {cuentaName(t.cuenta_destino)}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#6366F1", fontFamily: FF, flexShrink: 0 }}>{fmt(t.monto)}</div>
                  {confirmDelete === t.id ? (
                    <div style={{ display: "flex", gap: 3 }}>
                      <button type="button" onClick={(e) => { e.preventDefault(); handleDelete(t.id); }} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✓</button>
                      <button type="button" onClick={() => setConfirmDelete(null)} style={{ width: 28, height: 28, borderRadius: 8, border: "1px solid #E5E7EB", background: "#F3F4F6", color: "#6B7280", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(t.id)} style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${P.border}`, background: "transparent", color: P.textXs, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
